import { costMicros } from '@shared/pricing'
import { placedMessageId } from '@shared/tool-results'
import { type BoardView, type TranscriptEntry, type Trigger } from '@shared/session'
import { type Action } from './actions'
import { type StreamPart } from './store'

// The presentation layer of a session: per turn, what the person said and what
// the AI deliberately said back (`say`). Everything else stays in the trace.

export type Said = {
  id: string
  text: string
  live: boolean
  /** The board message this was also placed as (say with place / reply_to). */
  boardMessageId?: string
}

export type Turn = {
  turnId: string
  ts: number
  trigger: Trigger
  /** What the AI was told about the board this turn: a full snapshot or the changes since its last turn. */
  board: string | null
  /** The same, as data, when the server recorded it. */
  boardView: BoardView | null
  said: Said[]
  /** Every tool call of the turn, with its result once it has one; streaming calls included. */
  actions: Action[]
  /** null while the turn is still running. */
  end: { ts: number; error?: string; code?: 'context_full'; aborted?: boolean } | null
  costMicros: number
}

type ToolUse = {
  type: 'tool_use'
  id: string
  name: string
  input: { text?: unknown } & Record<string, unknown>
}

function isToolUse(b: unknown): b is ToolUse {
  return typeof b === 'object' && b !== null && (b as { type?: unknown }).type === 'tool_use'
}

export function buildTurns(
  entries: TranscriptEntry[],
  partials: Record<string, StreamPart> = {},
): Turn[] {
  const turns: Turn[] = []
  const byId = new Map<string, Turn>()
  for (const e of entries) {
    let turn = byId.get(e.turnId)
    if (!turn) {
      turn = {
        turnId: e.turnId,
        ts: e.ts,
        trigger: {},
        board: null,
        boardView: null,
        said: [],
        actions: [],
        end: null,
        costMicros: 0,
      }
      byId.set(e.turnId, turn)
      turns.push(turn)
    }
    switch (e.kind) {
      case 'turn_start':
        turn.trigger = e.trigger
        turn.ts = e.ts
        break
      case 'user':
        for (const b of e.content)
          if (b.type === 'text' && /^<board(_changes)?>/.test(b.text)) turn.board = b.text
        turn.boardView = e.board ?? null
        break
      case 'assistant':
        turn.costMicros += costMicros(e.model, e.usage) ?? 0
        for (const b of e.content) {
          if (!isToolUse(b)) continue
          turn.actions.push({ id: b.id, tool: b.name, input: b.input, result: null })
          if (b.name === 'say' && typeof b.input.text === 'string') {
            turn.said.push({ id: b.id, text: b.input.text, live: false })
          }
        }
        break
      case 'tool_results':
        for (const r of e.results) {
          const a = turn.actions.find((x) => x.id === r.tool_use_id)
          if (a) a.result = { content: r.content, error: !!r.is_error }
          const placed = placedMessageId(r.content)
          const said = turn.said.find((x) => x.id === r.tool_use_id)
          if (said && placed && !r.is_error) said.boardMessageId = placed
          // A say that failed (withheld, or a bad reply_to) never reached the person.
          if (said && r.is_error) turn.said = turn.said.filter((x) => x !== said)
        }
        break
      case 'turn_end':
        turn.end = {
          ts: e.ts,
          ...(e.error !== undefined && { error: e.error }),
          ...(e.code && { code: e.code }),
          ...(e.aborted && { aborted: true }),
        }
        break
      default:
        break
    }
  }
  // Text still streaming in: shown live, replaced once the message is stored.
  for (const [id, p] of Object.entries(partials)) {
    const t = byId.get(p.turnId)
    if (t && !t.actions.some((a) => a.id === id))
      t.actions.push({ id, tool: p.name, input: p.input, result: null })
    if (p.name !== 'say' || typeof p.input.text !== 'string' || !p.input.text) continue
    const turn = byId.get(p.turnId)
    if (!turn || turn.said.some((s) => s.id === id)) continue
    turn.said.push({ id, text: p.input.text, live: true })
  }
  return turns
}

/** How a board block reads in one line: a snapshot (and its size) or a number of changes. */
export function boardSummary(text: string): { kind: 'snapshot' | 'changes'; count: number } {
  const body = text.replace(/^<[^>]+>\n?|\n?<\/[^>]+>$/g, '')
  if (text.startsWith('<board_changes>'))
    return { kind: 'changes', count: body.split('\n').filter((l) => /^[+~-] /.test(l)).length }
  if (body.trim() === '(the board is empty)') return { kind: 'snapshot', count: 0 }
  // Top-level lines are entries; indented lines are card or message text.
  return { kind: 'snapshot', count: body.split('\n').filter((l) => /^(\w+) \S+/.test(l)).length }
}

/** Entries of one turn (or all), for the trace panel. */
export function turnEntries(entries: TranscriptEntry[], turnId: string | null): TranscriptEntry[] {
  return turnId ? entries.filter((e) => e.turnId === turnId) : entries
}
